// @ts-nocheck
import React, { ReactElement } from 'react';
import ReactDOM from 'react-dom/client';
import { Extents, LatLng } from '../../types/geo';
import { waitForCallBackOrExpire } from '../../utils/methods';
import MapViewSearchBox from './map-view-search-box';

export interface GoogleMapPlaceResult {
  coordinates: LatLng,
  zoom: number,
  label: string,
}

export interface GoogleMapLocalSettings {
  zoom: number,
  searchZoom: number,
  initialZoom: number,
}

export const getGoogleMapPlace = (
  results: google.maps.GeocoderResult[],
  status: google.maps.GeocoderStatus,
): GoogleMapPlaceResult => {
  if (status === google.maps.GeocoderStatus.OK && results.length) {
    return {
      coordinates: {
        lat: results[0].geometry.location.lat(),
        lng: results[0].geometry.location.lng(),
      },
      label: results[0].formatted_address || '',
      zoom: 12,
    };
  }
  return {
    coordinates: { lat: 0, lng: 0 },
    label: 'Location unavailable.',
    zoom: 2.5,
  } as GoogleMapPlaceResult;
};

export const getGoogleAutocompletePredictions = (
  predictions: google.maps.places.QueryAutocompletePrediction[] | null,
  status: google.maps.places.PlacesServiceStatus,
): any[] => {
  if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
    return predictions;
  }
  return [];
};

export const currentMapZoom = (
  extents: Extents,
  search = false,
): number => {
  if (search) {
    return extents.searchZoom;
  }
  return extents.latitude === 0 && extents.longitude === 0
    ? extents.initialZoom
    : extents.zoom;
};

// google maps InfoWindow handling logic

const getInfowindowsHtmlElements = (elementId: string) => {
  const recordCardWrapper = document.getElementById(elementId) as HTMLElement;
  const infoWindowOuterWrapper = recordCardWrapper?.parentNode?.parentNode as HTMLElement;
  const infoWindowComponent = infoWindowOuterWrapper?.parentNode as HTMLElement;
  const infoWindowElementWrapper = infoWindowComponent?.parentNode as HTMLElement;
  const infoWindowCloseButton = infoWindowOuterWrapper
    ? infoWindowOuterWrapper.nextSibling as HTMLElement
    : undefined;
  return {
    recordCardWrapper,
    infoWindowComponent,
    infoWindowCloseButton,
    infoWindowElementWrapper,
    infoWindowOuterWrapper,
  };
};

const hideDomElement = (element: HTMLElement) => {
  element.setAttribute('style', 'display: none');
};

const addMapPinCardMouseHoverLogic = (
  infoWindow: google.maps.InfoWindow,
  mouseOverCallBack: (setter: boolean) => void,
  recordCardWrapper: HTMLElement,
  element: HTMLElement,
) => {
  const root = ReactDOM.createRoot(recordCardWrapper);

  element.addEventListener('mouseleave', () => {
    root.unmount();
    infoWindow.close();
    mouseOverCallBack(false);
    return false;
  });
  element.addEventListener('mouseenter', () => {
    mouseOverCallBack(true);
  });
};

const overridesInfoWindowDefaultStyles = (
  infoWindowWrapper: HTMLElement,
  infoWindowComponent: HTMLElement,
) => {
  infoWindowWrapper
    .setAttribute('style', 'padding: 5px 0px 0px 0px!important; min-height: 205px!important');
  infoWindowComponent
    .setAttribute('style', 'min-height: 215px!important; overflow: unset!important; padding: 0px 16px 14px 16px!important; border-radius: 4px!important;');
};

// The logic below allows infowindow to stay open
// While the user keeps the mouse over the info card.
export const addReactComponentsAndListeners = async (
  infoWindow: google.maps.InfoWindow,
  mouseOverCallBack: (setter: boolean) => void,
  reactComponent: any, // this is typed for React.ReactNode. maybe a bug in React DOM types?
  contentTagId: string,
): Promise<any> => new Promise((resolve, reject) => {
  waitForCallBackOrExpire(() => document.getElementById(contentTagId), 400)
    .then(() => {
      const {
        recordCardWrapper,
        infoWindowComponent,
        infoWindowCloseButton,
        infoWindowElementWrapper,
        infoWindowOuterWrapper,
      } = getInfowindowsHtmlElements(contentTagId);

      if (infoWindowComponent && infoWindowCloseButton) {
        hideDomElement(infoWindowCloseButton);
        // Renders <MapRecordCard /> inside infowindow component wrapper element.
        ReactDOM.createRoot(recordCardWrapper).render(reactComponent);

        addMapPinCardMouseHoverLogic(
          infoWindow,
          mouseOverCallBack,
          infoWindowElementWrapper,
          recordCardWrapper,
        );

        overridesInfoWindowDefaultStyles(
          infoWindowOuterWrapper,
          infoWindowComponent,
        );
      }
      resolve(contentTagId);
    })
    .catch((e) => {
      console.error('<MapView /> infoWindow: Failed finding infoWindow to attach listeners.', e);
      reject(e);
    });
});

const openInfoWindow = (
  marker: google.maps.Marker,
  infoWindow: google.maps.InfoWindow,
  reactComponent: ReactElement,
  map: google.maps.Map,
  contentTagId: string,
  mouseOverCallBack: (setter: boolean) => void,
) => {
  infoWindow.open(map, marker);
  addReactComponentsAndListeners(infoWindow, mouseOverCallBack, reactComponent, contentTagId);
};

export const addInfoWindowElement = (
  marker: google.maps.Marker,
  infoWindow: google.maps.InfoWindow,
  reactComponent: ReactElement,
  contentTagId: string,
  map: google.maps.Map,
) => {
  let mouseOverInfoWindow: boolean;
  let markerTimeOut: NodeJS.Timeout;

  const mouseOverCallBack = (setter: boolean) => {
    mouseOverInfoWindow = setter;
  };
  marker.addListener('mouseover', () => {
    if (markerTimeOut) { clearTimeout(markerTimeOut); }
    openInfoWindow(marker, infoWindow, reactComponent, map, contentTagId, mouseOverCallBack);
  });
  marker.addListener('mouseout', () => {
    markerTimeOut = setTimeout(() => {
      if (!mouseOverInfoWindow) {
        infoWindow.close();
      }
    }, 500);
  });
};

export const injectInMapSearchField = (map: google.maps.Map) => {
  waitForCallBackOrExpire(() => document.getElementById('in-map-search-box'), 400)
    .then(() => {
      const inputFieldWrap = document.getElementById('in-map-search-box');
      ReactDOM.createRoot(inputFieldWrap as Element).render(<MapViewSearchBox />);
      // we need to wait for the map search box component to render this input
      return waitForCallBackOrExpire(() => document.getElementById('map-search-address'), 400);
    })
    .then(() => {
      const input = document.getElementById('map-search-address') as HTMLInputElement;
      const searchBox = new google.maps.places.SearchBox(input);
      map.controls[google.maps.ControlPosition.TOP_RIGHT].push(input);
      map.addListener('bounds_changed', () => {
        searchBox.setBounds(map.getBounds() as google.maps.LatLngBounds);
      });
      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces();
        if (places.length === 0) {
          return;
        }
        // For each place, get the icon, name and location.
        const bounds = new google.maps.LatLngBounds();

        places.forEach((place) => {
          if (!place.geometry || !place.geometry.location) {
            console.error('Returned place contains no geometry');
            return;
          }
          if (place.geometry.viewport) {
            // Only geocodes have viewport.
            bounds.union(place.geometry.viewport);
          } else {
            bounds.extend(place.geometry.location);
          }
        });
        map.fitBounds(bounds);
      });
    })
    .catch((e) => {
      console.error('<MapView /> in-map search field: Failed injecting field.', e);
    });
};
